import SignInButton from '@/components/button/SignInButton.tsx';
import { ReportProblemButton } from '@/components/feedback/ReportProblemButton.tsx';
import SendFeedbackSidePanel from '@/components/feedback/SendFeedbackSidePanel.tsx';
import LanguageSelector from '@/components/LanguageSelector';
import UserDropdownMenu from '@/components/menu/UserDropdownMenu';
import { SignInBeforeJoinPopoverContent } from '@/components/SignInBeforeJoinPopoverContent.tsx';
import { Popover, PopoverTrigger } from '@/components/ui/popover.tsx';
import { Separator } from '@/components/ui/separator';
import { usePageTitle } from '@/hook/usePageTitle.ts';
import { authService } from '@/service/AuthService.ts';
import { useAuthStore } from '@/store/AuthStore.ts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuLanguages, LuMenu, LuSparkles } from 'react-icons/lu';
import { Link, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from './ui/spinner';

interface HeaderProps {
  onMobileMenuClick?: () => void;
  enableSingInHint?: boolean;
}

const Header = ({ onMobileMenuClick, enableSingInHint }: HeaderProps) => {
  const user = useAuthStore((state) => state.user);
  const userLoading = useAuthStore((state) => state.userLoading);
  const pageTitle = usePageTitle();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showSingInHint, setShowSingInHint] = useState(false);

  useEffect(() => {
    (async () => {
      if (enableSingInHint) {
        setShowSingInHint(!(await authService.isAuthorized()));
      }
    })();
  }, [enableSingInHint]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 flex h-16 w-full items-center border-b bg-background p-2 z-40">
        <nav className="flex flex-grow text-lg font-medium items-center">
          {onMobileMenuClick && (
            <button
              onClick={onMobileMenuClick}
              className="mr-2 p-2 rounded-lg hover:bg-muted transition-colors md:hidden"
              aria-label={t('common.menu')}
            >
              <LuMenu size={24} />
            </button>
          )}
          <a href="#" className="flex items-center gap-2 font-semibold" onClick={() => (window.location.href = '/')}>
            <img src="/logo-no-text@2x.webp" alt="InterMind" width={20} height={20} />
            InterMIND
          </a>
          {pageTitle && (
            <span className="ml-6 hidden sm:flex items-center gap-6">
              <span className="w-px h-6 bg-muted-foreground/20"></span>
              <span className="text-muted-foreground">{pageTitle}</span>
            </span>
          )}
        </nav>
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/pricing" className="text-sm text-primary hover:underline font-medium">
              {t('homepage.viewPricing')}
            </Link>
          </div>

          {!userLoading && user && (
            <>
              <div className="hidden sm:flex landscape:flex items-center gap-2 sm:gap-4">
                <Link to="/history" className="text-sm text-primary hover:underline font-medium">
                  {t('homepage.history')}
                </Link>
              </div>
            </>
          )}

          {(user || !userLoading) && <Separator orientation="vertical" className="flex h-6" />}

          {import.meta.env.VITE_APP_ASK_FEATURE_ENABLED === 'true' && (
            <LuSparkles
              className="w-5 h-5 text-muted-foreground hover:text-primary hover:cursor-pointer transition-colors"
              title={t('homepage.askAnything')}
              onClick={() => navigate('/ask')}
            />
          )}

          <ReportProblemButton />

          <div className="hidden sm:flex items-center gap-2">
            <LanguageSelector>
              <LuLanguages className="w-5 h-5 text-muted-foreground" />
            </LanguageSelector>
          </div>

          <div className="min-w-[3rem] flex justify-center items-center">
            {userLoading && <LoadingSpinner size={12} className=" text-indigo-600 stroke-1" />}
            {!userLoading &&
              (user ? (
                <UserDropdownMenu user={user} />
              ) : (
                <Popover open={showSingInHint}>
                  <PopoverTrigger>
                    <SignInButton asChild />
                  </PopoverTrigger>
                  <SignInBeforeJoinPopoverContent onOk={() => setShowSingInHint(false)} />
                </Popover>
              ))}
          </div>
        </div>
      </header>
      <SendFeedbackSidePanel />
    </>
  );
};

export default Header;
